/**
 * MODULE: _shared/frontend-shell/src/features/crm/hooks/useCrmAccountContracts.ts
 * PURPOSE: React Query hook for account-scoped CRM contracts
 * PUBLIC_API: useCrmAccountContracts
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  crmCreateContract,
  crmDeleteContract,
  crmListContracts,
  crmPatchContract,
  type CrmContract,
  type CrmContractStatus,
} from '@shared/lib/frontend/api/crmContractsApi';

export function useCrmAccountContracts(accountId: string | null | undefined, enabled = true) {
  const queryClient = useQueryClient();
  const queryKey = ['crm', 'contracts', accountId];

  const query = useQuery({
    queryKey,
    queryFn: () => crmListContracts({ accountId: accountId!, limit: 50 }),
    enabled: Boolean(enabled && accountId),
    staleTime: 30_000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const createMutation = useMutation({
    mutationFn: (body: {
      title: string;
      status?: CrmContractStatus;
      value?: number;
      currencyCode?: string;
      renewalDateISO?: string;
      autoRenew?: boolean;
    }) => crmCreateContract({ accountId: accountId!, ...body }),
    onSuccess: invalidate,
  });

  const patchMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<CrmContract> }) => crmPatchContract(id, patch),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => crmDeleteContract(id),
    onSuccess: invalidate,
  });

  return {
    contracts: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    createContract: createMutation.mutateAsync,
    creating: createMutation.isPending,
    patchContract: patchMutation.mutateAsync,
    patching: patchMutation.isPending,
    deleteContract: deleteMutation.mutateAsync,
  };
}
